import { Component, OnInit, HostListener, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { IconComponent } from '../icon';
import { AuthService, User } from '@/services/auth.service';
import { ButtonComponent } from '../button/button.component';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [CommonModule, RouterModule, IconComponent, ButtonComponent],
  template: `
    <header
      class="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50"
    >
      <div class="max-w-7xl mx-auto px-4 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center md:gap-4">
          <!-- back button -->
          <button
            (click)="goBack()"
            *ngIf="showBackButton"
            class="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <app-icon
              name="arrow-left"
              className="w-6 h-6 text-gray-700"
            ></app-icon>
          </button>

          <!-- Logo -->
          <a
            [routerLink]="['/portal']"
            class="flex items-center gap-3 hover:opacity-80 transition-opacity"
          >
            <img
              src="https://zoomeduca.com.br/logo.svg"
              alt="Zoom Educa"
              class="h-7 sm:h-8 md:h-10 object-contain"
            />
          </a>
          </div>

          <!-- Right side: Logo, Notifications & Profile -->
          <div class="flex items-center gap-3">
            <!-- Not Logged In -->
            <div *ngIf="!currentUser" class="flex items-center gap-2">
              <!-- Desktop buttons -->
              <div class="hidden sm:flex items-center gap-2">
                <button
                  app-button
                  routerLink="/cadastro"
                  className="text-sm font-medium whitespace-nowrap"
                >
                  Criar Conta
                </button>
                <a
                  app-button
                  routerLink="/login"
                  className="text-sm font-medium whitespace-nowrap"
                >
                  Entrar
                </a>
              </div>

              <!-- Mobile dropdown -->
              <div class="sm:hidden relative auth-dropdown-container">
                <button
                  (click)="toggleAuthDropdown()"
                  class="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="Menu"
                >
                  <app-icon
                    name="menu"
                    className="w-6 h-6 text-gray-700"
                  ></app-icon>
                </button>

                <!-- Dropdown Menu -->
                <div
                  *ngIf="showAuthDropdown"
                  class="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2"
                >
                  <a
                    routerLink="/cadastro"
                    (click)="closeAuthDropdown()"
                    class="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <app-icon name="user-plus" className="w-4 h-4"></app-icon>
                    Criar Conta
                  </a>
                  <a
                    routerLink="/login"
                    (click)="closeAuthDropdown()"
                    class="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <app-icon name="log-in" className="w-4 h-4"></app-icon>
                    Entrar
                  </a>
                </div>
              </div>
            </div>

            <!-- Logged In -->
            <ng-container *ngIf="currentUser">
              <!-- in case candidate, show button to go to candidate portal - Hidden on very small screens -->
              <div
                *ngIf="currentUser.role === 'candidate'"
                class="hidden md:block"
              >
                <a
                  app-button
                  [routerLink]="['/portal/candidato']"
                  routerLinkActive="!border-[#4A90E2] !text-[#4A90E2] bg-[#357ABD10]"
                  variant="outline"
                  className="text-sm font-medium whitespace-nowrap"
                >
                  <div class="flex flex-1 items-center gap-2">
                    <svg
                      width="20"
                      height="20"
                      class="sm:w-6 sm:h-6"
                      viewBox="0 0 256 256"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                    >
                      <path
                        d="M220 110.933v54.521a7.96 7.96 0 0 1-1.59 4.787C211.675 179.205 183.254 212 128 212s-83.674-32.795-90.41-41.759a7.96 7.96 0 0 1-1.59-4.787v-54.52L128 160Z"
                        opacity=".2"
                      />
                      <path
                        d="m251.765 88.941-120-64a8 8 0 0 0-7.53 0l-120 64a8 8 0 0 0 0 14.118L28 115.733v49.721a16.06 16.06 0 0 0 3.194 9.593C39.12 185.593 69.52 220 128 220a128.7 128.7 0 0 0 52-10.456V240a8 8 0 0 0 16 0v-38.933a110.4 110.4 0 0 0 28.805-26.019 16.06 16.06 0 0 0 3.195-9.594v-49.72l23.765-12.675a8 8 0 0 0 0-14.118M128 204c-51.066 0-77.216-29.518-84-38.546v-41.188l80.235 42.793a8 8 0 0 0 7.53 0L180 141.333v50.513C166.873 198.667 149.693 204 128 204m84.015-38.565A89.7 89.7 0 0 1 196 181.54V132.8l16.003-8.535Zm4.206-61.553-.057.03L188 118.935 131.765 88.94a8 8 0 0 0-7.53 14.118L171 128l-43 22.934-88.164-47.021-.057-.03L25 96l103-54.934L231 96Z"
                      />
                    </svg>
                    Área do Candidato
                  </div>
                </a>
              </div>

              <!-- Notification Bell -->
              <button
                class="relative p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Notificações"
                [routerLink]="['/portal/notificacoes']"
                routerLinkActive="bg-gray-100"
              >
                <app-icon
                  name="bell"
                  className="w-5 h-5 text-gray-600"
                ></app-icon>
                <span
                  *ngIf="notificationCount > 0"
                  class="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"
                ></span>
              </button>

              <!-- Profile Dropdown -->
              <div class="relative profile-dropdown-container">
                <button
                  (click)="toggleDropdown()"
                  class="flex items-center gap-2 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <div
                    [class]="
                      getAvatarColorClass(currentUser.role) +
                      ' w-8 h-8 rounded-full flex items-center justify-center'
                    "
                  >
                    <span class="text-sm font-semibold text-white">
                      {{
                        getInitials(currentUser.name || currentUser.username)
                      }}
                    </span>
                  </div>
                  <span
                    class="text-sm font-medium text-gray-700 hidden sm:inline"
                  >
                    {{ currentUser.name || currentUser.username }}
                  </span>
                  <app-icon
                    name="chevron-down"
                    className="w-4 h-4 text-gray-500"
                  ></app-icon>
                </button>

                <!-- Dropdown Menu -->
                <div
                  *ngIf="showDropdown"
                  class="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2"
                >
                  <div class="px-4 py-3 border-b border-gray-200">
                    <p class="text-sm font-semibold text-gray-900">
                      {{ currentUser.name || currentUser.username }}
                    </p>
                    <p *ngIf="currentUser.email" class="text-xs text-gray-500">
                      {{ currentUser.email }}
                    </p>
                    <p class="text-xs text-blue-600 mt-1">
                      {{ getRoleLabel(currentUser.role) }}
                    </p>
                  </div>

                  <div class="py-1">
                    <!-- Área do Candidato - visible on small screens (< md) -->
                    <a
                      *ngIf="currentUser.role === 'candidate'"
                      [routerLink]="['/portal/candidato']"
                      (click)="closeDropdown()"
                      class="flex md:hidden items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-b border-gray-100"
                    >
                      <svg
                        width="20"
                        height="20"
                        class="sm:w-6 sm:h-6"
                        viewBox="0 0 256 256"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="currentColor"
                      >
                        <path
                          d="M220 110.933v54.521a7.96 7.96 0 0 1-1.59 4.787C211.675 179.205 183.254 212 128 212s-83.674-32.795-90.41-41.759a7.96 7.96 0 0 1-1.59-4.787v-54.52L128 160Z"
                          opacity=".2"
                        />
                        <path
                          d="m251.765 88.941-120-64a8 8 0 0 0-7.53 0l-120 64a8 8 0 0 0 0 14.118L28 115.733v49.721a16.06 16.06 0 0 0 3.194 9.593C39.12 185.593 69.52 220 128 220a128.7 128.7 0 0 0 52-10.456V240a8 8 0 0 0 16 0v-38.933a110.4 110.4 0 0 0 28.805-26.019 16.06 16.06 0 0 0 3.195-9.594v-49.72l23.765-12.675a8 8 0 0 0 0-14.118M128 204c-51.066 0-77.216-29.518-84-38.546v-41.188l80.235 42.793a8 8 0 0 0 7.53 0L180 141.333v50.513C166.873 198.667 149.693 204 128 204m84.015-38.565A89.7 89.7 0 0 1 196 181.54V132.8l16.003-8.535Zm4.206-61.553-.057.03L188 118.935 131.765 88.94a8 8 0 0 0-7.53 14.118L171 128l-43 22.934-88.164-47.021-.057-.03L25 96l103-54.934L231 96Z"
                        />
                      </svg>
                      Área do Candidato
                    </a>
                    <a
                      *ngIf="currentUser.role === 'candidate'"
                      [routerLink]="['/portal/candidato/meus-dados']"
                      (click)="closeDropdown()"
                      class="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <app-icon name="user" className="w-4 h-4"></app-icon>
                      Meus Dados
                    </a>
                    <a
                      *ngIf="currentUser.role === 'admin'"
                      [routerLink]="['/portal/admin']"
                      (click)="closeDropdown()"
                      class="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <app-icon
                        name="layout-dashboard"
                        className="w-4 h-4"
                      ></app-icon>
                      Painel Admin
                    </a>
                    <a
                      *ngIf="currentUser.role === 'evaluator'"
                      [routerLink]="['/portal/avaliador']"
                      (click)="closeDropdown()"
                      class="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <app-icon
                        name="clipboard-check"
                        className="w-4 h-4"
                      ></app-icon>
                      Painel de Avaliação
                    </a>
                    <button
                      (click)="logout()"
                      class="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <app-icon name="log-out" className="w-4 h-4"></app-icon>
                      Sair
                    </button>
                  </div>
                </div>
              </div>
            </ng-container>

            <!-- Recrutamento Logo -->
            <img
              src="/recrutamento_logo.png"
              alt="Recrutamento"
              class="h-6 sm:h-8 md:h-10 object-contain"
            />
          </div>
        </div>
      </div>
    </header>
  `,
})
export class NavbarComponent implements OnInit {
  currentUser: User | null = null;
  notificationCount: number = 0;
  showDropdown = false;
  showAuthDropdown = false;
  @Input() showBackButton: boolean = false;

  constructor(private authService: AuthService, private router: Router) {}

  ngOnInit() {
    this.authService.currentUser$.subscribe((user) => {
      this.currentUser = user;
      this.showDropdown = false; // Close dropdown on user change
    });
  }

  goBack() {
    this.router.navigate(['/portal']);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const clickedInsideProfile = target.closest('.profile-dropdown-container');
    const clickedInsideAuth = target.closest('.auth-dropdown-container');

    if (!clickedInsideProfile && this.showDropdown) {
      this.showDropdown = false;
    }

    if (!clickedInsideAuth && this.showAuthDropdown) {
      this.showAuthDropdown = false;
    }
  }

  getInitials(name?: string): string {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((n) => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }

  getAvatarColorClass(role: string): string {
    switch (role) {
      case 'admin':
        return 'bg-gradient-to-br from-purple-500 to-purple-600';
      case 'evaluator':
        return 'bg-gradient-to-br from-green-500 to-green-600';
      case 'supervisor':
        return 'bg-gradient-to-br from-orange-500 to-orange-600';
      default:
        return 'bg-gradient-to-br from-blue-500 to-blue-600';
    }
  }

  getRoleLabel(role: string): string {
    switch (role) {
      case 'admin':
        return 'Administrador';
      case 'evaluator':
        return 'Avaliador';
      case 'supervisor':
        return 'Fiscal';
      case 'candidate':
        return 'Candidato';
      default:
        return '';
    }
  }
  toggleAuthDropdown() {
    this.showAuthDropdown = !this.showAuthDropdown;
  }

  closeAuthDropdown() {
    this.showAuthDropdown = false;
  }
  toggleDropdown() {
    this.showDropdown = !this.showDropdown;
  }

  closeDropdown() {
    this.showDropdown = false;
  }

  logout() {
    this.authService.logout();
    this.closeDropdown();
    this.router.navigate(['/portal']);
  }
}
